import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Bot, Loader2, KeyRound, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { resetPassword, formatApiError } from "@/lib/api";
import { COPYRIGHT } from "@/lib/legalTexts";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Şifre en az 6 karakter olmalıdır."); return; }
    if (pw !== pw2) { toast.error("Şifreler eşleşmiyor."); return; }
    setBusy(true);
    try {
      await resetPassword(token, pw);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative z-[1]">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="h-11 w-11 rounded-md bg-[var(--pos)] flex items-center justify-center">
            <Bot className="h-6 w-6 text-black" strokeWidth={2.4} />
          </div>
          <div>
            <div className="font-head font-black text-xl tracking-tight">CRYPTOSENSE</div>
            <div className="text-[10px] text-[var(--accent)] tracking-[0.25em]">AI TRADING DESK</div>
          </div>
        </div>

        <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-7">
          {!token ? (
            <div className="text-center space-y-3 py-4">
              <AlertTriangle className="h-10 w-10 text-[var(--warn)] mx-auto" />
              <p className="text-sm text-[var(--text-dim)]">Geçersiz bağlantı. Lütfen sıfırlama e-postasındaki bağlantıyı kullanın.</p>
              <Button onClick={() => nav("/forgot-password")} className="w-full h-11 bg-[var(--pos)] text-black font-bold hover:bg-[var(--pos-hover)] hover:text-black">
                Yeni Bağlantı İste
              </Button>
            </div>
          ) : done ? (
            <div className="text-center space-y-4 py-4" data-testid="reset-success">
              <CheckCircle2 className="h-12 w-12 text-[var(--pos)] mx-auto" />
              <h1 className="font-head font-bold text-xl">Şifreniz Güncellendi</h1>
              <p className="text-sm text-[var(--text-dim)]">Yeni şifrenizle giriş yapabilirsiniz.</p>
              <Button data-testid="reset-go-login" onClick={() => nav("/login")}
                className="w-full h-11 bg-[var(--pos)] text-black font-bold hover:bg-[var(--pos-hover)] hover:text-black">
                Giriş Yap
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <h1 className="font-head font-bold text-2xl">Yeni Şifre</h1>
                <p className="text-sm text-[var(--text-dim)] mt-1">Hesabınız için yeni bir şifre belirleyin</p>
              </div>
              <div>
                <Label className="text-xs text-[var(--text-dim)]">Yeni Şifre (en az 6 karakter)</Label>
                <Input data-testid="reset-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required
                  className="mt-1 bg-[var(--app-bg)] border-[var(--border)] font-mono text-sm h-11" />
              </div>
              <div>
                <Label className="text-xs text-[var(--text-dim)]">Yeni Şifre (tekrar)</Label>
                <Input data-testid="reset-password2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required
                  className="mt-1 bg-[var(--app-bg)] border-[var(--border)] font-mono text-sm h-11" />
              </div>
              <Button data-testid="reset-submit" type="submit" disabled={busy}
                className="w-full h-12 bg-[var(--pos)] text-black font-bold hover:bg-[var(--pos-hover)] hover:text-black">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-4 w-4 mr-1.5" /> Şifreyi Güncelle</>}
              </Button>
            </form>
          )}
        </div>
        <p className="mt-6 text-center text-[10px] text-[var(--text-faint)] font-mono">{COPYRIGHT}</p>
      </div>
    </div>
  );
}
